import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import api from "../lib/apiClient";
import type { IncidentAttachmentEntry } from "../types/incident";

/**
 * US-090 (CAPA) — incident evidence. Upload + soft-delete invalidate the
 * incident detail query (attachments are embedded there). Downloads stream
 * through the backend proxy as a blob (no presigned URLs).
 */
export function useUploadIncidentAttachment(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Failed to upload evidence" },
    mutationFn: async (input: { file: File; description?: string }) => {
      const formData = new FormData();
      formData.append("file", input.file);
      if (input.description) formData.append("description", input.description);
      const { data } = await api.post(
        `/incidents/${incidentId}/attachments`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data.data as IncidentAttachmentEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", incidentId] });
      notifications.show({ message: "Evidence uploaded.", color: "green" });
    },
  });
}

export function useDeleteIncidentAttachment(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Failed to delete evidence" },
    mutationFn: async (attachmentId: string) => {
      await api.delete(`/incidents/${incidentId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", incidentId] });
      notifications.show({ message: "Evidence removed.", color: "green" });
    },
  });
}

/** Stream a blob from a proxied attachment URL and trigger a browser download. */
async function downloadBlob(url: string, fileName: string): Promise<void> {
  const response = await api.get(url, { responseType: "blob" });
  const objectUrl = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/** App-side evidence download (incident:read). */
export function downloadIncidentAttachment(
  incidentId: string,
  attachmentId: string,
  fileName: string,
): Promise<void> {
  return downloadBlob(
    `/incidents/${incidentId}/attachments/${attachmentId}`,
    fileName,
  );
}

/** Auditor-side evidence download (active grant + incidents scope). */
export function downloadAuditIncidentAttachment(
  facilityId: string,
  incidentId: string,
  attachmentId: string,
  fileName: string,
): Promise<void> {
  return downloadBlob(
    `/audit/facilities/${facilityId}/incidents/${incidentId}/attachments/${attachmentId}`,
    fileName,
  );
}
